import React, { useEffect, ReactNode } from 'react';
import { FaCheckCircle, FaTimesCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

/**
 * How `details` render. 'plain' (default) keeps the pill toast with a small
 * details box; 'warning' is the Figma "toast with subtext" layout (DRT-VD node
 * 21313-65579, ECOPD-18560): a title row with each detail in its own light-red
 * box below. Opt-in per call, so other toasts are unchanged.
 */
export type ToastDetailsVariant = 'plain' | 'warning';

export interface ToastProps {
  message: ReactNode;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
  details?: string[];
  detailsVariant?: ToastDetailsVariant;
}

const toastVariants = {
  success: {
    base: 'bg-success border-success text-on-semantic',
    text: 'text-on-semantic',
    Icon: FaCheckCircle,
  },
  error: {
    base: 'bg-error border-error text-on-semantic',
    text: 'text-on-semantic',
    Icon: FaTimesCircle,
  },
  info: {
    base: 'bg-primary border-primary text-on-primary',
    text: 'text-on-primary',
    Icon: FaInfoCircle,
  },
};

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'success',
  duration = 3000,
  onClose,
  details,
  detailsVariant = 'plain',
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const variant = toastVariants[type];
  const { Icon } = variant;

  if (detailsVariant === 'warning' && details && details.length > 0) {
    return (
      <div
        className={`flex flex-col gap-1 px-3 py-2 rounded-sm ${variant.base} min-w-[320px] max-w-lg animate-slide-down`}
        data-testid="toast-with-warnings"
      >
        <div className="flex items-center gap-2">
          {/* Decorative icon — type is already conveyed by the live-region role (status/alert) */}
          <Icon className={variant.text} size={20} aria-hidden="true" />
          <span className={`flex-1 text-sm-14 font-medium ${variant.text} whitespace-pre-line`}>{message}</span>
          <button
            onClick={onClose}
            className={`${variant.text} hover:opacity-70 transition-opacity`}
            data-testid="toast-close-button"
            aria-label="Dismiss"
          >
            <FaTimes size={16} aria-hidden="true" />
          </button>
        </div>
        {/* Opaque surface under the tinted box so the error tint does not mix
            with the toast colour behind it. */}
        {details.map((detail, index) => (
          <div key={index} className="rounded-lg bg-surface shadow-md">
            <div className="rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-xs text-text-secondary">
              {detail}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg ${variant.base} min-w-[320px] max-w-lg animate-slide-down`}
    >
      {/* Decorative icon — type is already conveyed by the live-region role (status/alert) */}
      <Icon className={variant.text} size={18} aria-hidden="true" />
      <div className="flex-1">
        <span className={`text-sm font-medium ${variant.text} whitespace-pre-line`}>{message}</span>
        {details && details.length > 0 && (
          <div className="mt-2 p-2 bg-surface-primary rounded text-xs">
            {details.map((detail, index) => (
              <div key={index}>{detail}</div>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        className={`${variant.text} hover:opacity-70 transition-opacity ml-2`}
        data-testid="toast-close-button"
        aria-label="Dismiss"
      >
        <FaTimes size={14} aria-hidden="true" />
      </button>
    </div>
  );
};

export default Toast;